// Portal-scoped auth utilities (student | teacher)
import axios from 'axios';
import { apiUrl } from '@/api/config';
import { clearAnonymousUserPreferences } from './anonymousAuth';
import { Device } from '@capacitor/device';

export const getPortalType = (currentPath) => {
  let path = currentPath || '';
  if (!path || path === '/' || path.startsWith('/login') || path.startsWith('login')) {
    path = window.location.hash || window.location.pathname || '';
  }
  
  // Clean up path from hash and params
  if (path.includes('#')) {
    path = path.split('#')[1] || '';
  }
  path = path.split('?')[0];
  
  // Standardize path start
  if (path.startsWith('/')) path = path.substring(1);

  if (path.startsWith('admin')) return 'admin';
  if (path.startsWith('teacher')) return 'teacher';
  if (path.startsWith('student')) return 'student';
  if (path.startsWith('b2b')) return 'b2b';
  if (path.startsWith('guardian')) return 'guardian';
  if (path.startsWith('affiliate') || path.startsWith('affiliat') || path.startsWith('affliat')) return 'affiliate';
  return 'student'; // Default
};

// Check if localStorage is available
export const isLocalStorageAvailable = () => {
  try {
    const test = '__localStorage_test__';
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch {
    return false;
  }
};

let isLoggingOut = false;
export const getIsLoggingOut = () => isLoggingOut;
export const setIsLoggingOut = (val) => { isLoggingOut = Boolean(val); };

const tokenKey = (portal) => `${portal}_token`;
const refreshKey = (portal) => `${portal}_refresh_token`;
const userKey = (portal) => `${portal}_user`;

export const setAuthTokens = (portal, accessToken, refreshToken, userData) => {
  isLoggingOut = false;
  if (!isLocalStorageAvailable()) {
    console.warn('localStorage is not available');
    return;
  }
  try {
    localStorage.setItem(tokenKey(portal), accessToken);
    localStorage.setItem(refreshKey(portal), refreshToken);
    localStorage.setItem(userKey(portal), JSON.stringify(userData || {}));
    // Clear guest session and anonymous preferences when user logs in
    localStorage.removeItem('is_guest_session');
    localStorage.removeItem('bootstrap_init_data');
    localStorage.removeItem('bootstrap_init_data_v2');
    clearAnonymousUserPreferences();
    
    // Reset Resume Learning Modal shown state for new login
    try {
      sessionStorage.removeItem('resume_modal_shown');
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith('budlee-v2-content-cache:'))
        .forEach((key) => sessionStorage.removeItem(key));
    } catch (e) {}

    // Dispatch login success event for context listeners
    window.dispatchEvent(new CustomEvent('login_success', { detail: { portal, userData } }));

    import('../analytics/analytics')
      .then(({ identifyAnalyticsUser }) => identifyAnalyticsUser({ portal, userData }))
      .catch((error) => console.warn('Analytics identify failed:', error));
    
    // If student, trigger guest data migration to account
    if (portal === 'student') {
      import('./guestMigration').then(({ migrateGuestData }) => {
        migrateGuestData().catch(err => console.error('Guest sync failed:', err));
      });
    }
  } catch (error) {
    console.error('Error setting auth tokens:', error);
    // If localStorage is not available, silently fail
  }
};

export const getAuthToken = (portal) => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const token = localStorage.getItem(tokenKey(portal));
    if (token) return token;
    // Fallback: If querying b2b token and teacher token exists with institute, allow sharing
    if (portal === 'b2b') {
      const teacherToken = localStorage.getItem(tokenKey('teacher'));
      if (teacherToken) {
        const teacherUser = getUserData('teacher');
        if (teacherUser?.institute) return teacherToken;
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting auth token:', error);
    return null;
  }
};

export const getRefreshToken = (portal) => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const token = localStorage.getItem(refreshKey(portal));
    if (token) return token;
    if (portal === 'b2b') {
      const teacherUser = getUserData('teacher');
      if (teacherUser?.institute) {
        return localStorage.getItem(refreshKey('teacher'));
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting refresh token:', error);
    return null;
  }
};

export const getUserData = (portal) => {
  if (!isLocalStorageAvailable()) return null;
  try {
    const raw = localStorage.getItem(userKey(portal));
    if (raw) {
      try {
        return JSON.parse(raw);
      } catch {
        return null;
      }
    }
    // Fallback: If querying b2b user and teacher user exists with institute
    if (portal === 'b2b') {
      const teacherRaw = localStorage.getItem(userKey('teacher'));
      if (teacherRaw) {
        try {
          const parsed = JSON.parse(teacherRaw);
          if (parsed?.institute) return parsed;
        } catch {
          return null;
        }
      }
    }
    return null;
  } catch (error) {
    console.error('Error getting user data:', error);
    return null;
  }
};

export const updateUserData = (portal, updates) => {
  if (isLoggingOut || !isLocalStorageAvailable()) return;
  try {
    const currentData = getUserData(portal) || {};
    const updatedData = { ...currentData, ...updates };
    localStorage.setItem(userKey(portal), JSON.stringify(updatedData));
    window.dispatchEvent(new CustomEvent('profileUpdated', { detail: { portal, userData: updatedData } }));
  } catch (error) {
    console.error('Error updating user data:', error);
  }
};

export const clearAuthTokens = (portal) => {
  if (!isLocalStorageAvailable()) return;
  try {
    localStorage.removeItem(tokenKey(portal));
    localStorage.removeItem(refreshKey(portal));
    localStorage.removeItem(userKey(portal));
    // Clear subject/chapter selection when student logs out
    if (portal === 'student') {
      localStorage.removeItem('student_subject_chapter_selection');
    }
  } catch (error) {
    console.error('Error clearing auth tokens:', error);
    // Continue even if clearing fails
  }
};

// Parse JWT token helper
export const parseJwt = (token) => {
  if (!token) return null;
  try {
    const base64Url = token.split('.')[1];
    if (!base64Url) return null;
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function(c) {
        return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (e) {
    return null;
  }
};

// Check if JWT token is expired (leeway of 10 seconds)
export const isTokenExpired = (token) => {
  const parsed = parseJwt(token);
  if (!parsed || !parsed.exp) return true;
  // exp is in seconds, Date.now() is in milliseconds. Allow 10s leeway.
  return parsed.exp * 1000 - 10000 < Date.now();
};

export const ALL_PORTALS = ['student', 'teacher', 'admin', 'b2b', 'guardian', 'affiliate'];

export const hasAnyStoredToken = () => {
  if (!isLocalStorageAvailable()) return false;
  for (const p of ALL_PORTALS) {
    if (localStorage.getItem(tokenKey(p)) || localStorage.getItem(refreshKey(p))) {
      return true;
    }
  }
  return !!(
    localStorage.getItem('token') ||
    localStorage.getItem('access_token') ||
    localStorage.getItem('refresh_token')
  );
};

export const hasAnyStoredAuthToken = () => {
  if (!isLocalStorageAvailable()) return false;
  for (const p of ALL_PORTALS) {
    if (localStorage.getItem(tokenKey(p))) {
      return true;
    }
  }
  return !!(
    localStorage.getItem('token') ||
    localStorage.getItem('access_token')
  );
};

export const isAuthenticated = (portal) => {
  if (!isLocalStorageAvailable()) return false;
  // If in the middle of a logout teardown, immediately treat session as unauthenticated
  if (isLoggingOut) return false;
  const token = getAuthToken(portal);
  if (!token) return false;
  
  // Stricter check: If access token is expired, check if refresh token is present and valid
  if (isTokenExpired(token)) {
    const refreshToken = getRefreshToken(portal);
    // If no refresh token exists either, or refresh token is also expired, the session is dead
    if (!refreshToken || isTokenExpired(refreshToken)) return false;
  }
  
  return true;
};

export const getAuthHeaders = (portal) => {
  const token = getAuthToken(portal);
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Check if any user session exists (Student, Teacher, Admin, B2B, Guardian, Affiliate)
export const hasAnySession = () => {
  return ALL_PORTALS.some(portal => isAuthenticated(portal));
};

// Get the active user's portal type (returns 'student', 'teacher', 'affiliate', etc., or null)
// Uses both URL path as a hint and authentication status
export const getActivePortal = () => {
  const isGuestSession = localStorage.getItem('is_guest_session') === 'true';
  if (isGuestSession) return null;

  // First try URL path if it explicitly matches a portal section
  const currentPath = window.location.hash ? window.location.hash.split('?')[0].replace('#', '') : window.location.pathname;
  const isExplicitPath = currentPath.includes('/teacher') || currentPath.includes('/student') || currentPath.includes('/admin') || currentPath.includes('/b2b') || currentPath.includes('/guardian') || currentPath.includes('/affiliate');
  const portalFromPath = getPortalType(currentPath);
  if (isExplicitPath && portalFromPath && getAuthToken(portalFromPath)) return portalFromPath;

  // Fallback to checking active authenticated sessions in order
  if (isAuthenticated('affiliate')) return 'affiliate';
  if (isAuthenticated('teacher')) return 'teacher';
  if (isAuthenticated('student')) return 'student';
  if (isAuthenticated('admin')) return 'admin';
  if (isAuthenticated('b2b')) return 'b2b';
  if (isAuthenticated('guardian')) return 'guardian';

  // Last resort: check if tokens exist
  if (getAuthToken('affiliate')) return 'affiliate';
  if (getAuthToken('teacher')) return 'teacher';
  if (getAuthToken('student')) return 'student';
  if (getAuthToken('admin')) return 'admin';
  if (getAuthToken('b2b')) return 'b2b';
  if (getAuthToken('guardian')) return 'guardian';

  return null;
};

// Check if the current user or active view context is a teacher
export const isTeacherUser = (explicitProp = false) => {
  if (explicitProp) return true;
  if (typeof window === 'undefined') return false;
  try {
    const hash = window.location.hash || '';
    const pathname = window.location.pathname || '';
    const fullPath = (hash + pathname).toLowerCase();
    if (fullPath.includes('/teacher')) return true;

    const portalType = getPortalType(hash || pathname);
    if (portalType === 'teacher') return true;
    if (getActivePortal() === 'teacher') return true;
    if (isAuthenticated('teacher')) return true;
    if (Boolean(getAuthToken('teacher'))) return true;

    const teacherData = getUserData('teacher');
    if (
      teacherData?.user_type === 'teacher' ||
      teacherData?.role?.toLowerCase() === 'teacher' ||
      teacherData?.account_type === 'teacher' ||
      teacherData?.is_teacher
    ) {
      return true;
    }

    const currentPortalData = getUserData(portalType);
    if (
      currentPortalData?.user_type === 'teacher' ||
      currentPortalData?.role?.toLowerCase() === 'teacher' ||
      currentPortalData?.account_type === 'teacher' ||
      currentPortalData?.is_teacher
    ) {
      return true;
    }
  } catch {
    return false;
  }
  return false;
};

// Get home page path for a portal type
export const getHomePagePath = (portal) => {
  switch (portal) {
    case 'student': return '/student/learn';
    case 'teacher': return '/teacher/teach';
    case 'admin': return '/admin/dashboard';
    case 'b2b': return '/b2b/dashboard';
    case 'guardian': return '/guardian/dashboard';
    case 'affiliate': return '/affiliate/dashboard';
    default: return '/login';
  }
};

// Clear all sessions (all portals)
export const clearAllSessions = () => {
  isLoggingOut = true;
  activeRefreshPromise = null;
  activeRefreshResolver = null;

  if (!isLocalStorageAvailable()) return;
  try {
    clearAuthTokens('student');
    clearAuthTokens('teacher');
    clearAuthTokens('admin');
    clearAuthTokens('b2b');
    clearAuthTokens('guardian');
    clearAuthTokens('affiliate');
    clearFingerprintCache();
    // Also clear any legacy keys
    localStorage.removeItem('token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem('is_guest_session');
    localStorage.removeItem('student_board_id');
    localStorage.removeItem('student_grade_id');
    // Clear subject/chapter selection
    localStorage.removeItem('student_subject_chapter_selection');
    // Clear bootstrap init data to prevent stale data between accounts
    localStorage.removeItem('bootstrap_init_data');
    localStorage.removeItem('bootstrap_init_data_v2');
    
    // Clear Resume Learning Modal state on logout
    try {
      sessionStorage.removeItem('resume_modal_shown');
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith('budlee-v2-content-cache:'))
        .forEach((key) => sessionStorage.removeItem(key));
    } catch (e) {}

    // Dispatch sessionLoggedOut event for any mounted listeners
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('sessionLoggedOut'));
    }
  } catch (error) {
    console.error('Error clearing all sessions:', error);
    // Continue even if clearing fails
  }
};

// Unified Hard Teardown for Clean Logouts across all portals
export const performLogoutTeardown = async (redirectPath = '/#/login') => {
  try {
    isLoggingOut = true;
    clearAllSessions();

    // On native Capacitor, clear native Google Sign-In cached session
    if (typeof window !== 'undefined' && window.Capacitor?.isNativePlatform?.()) {
      try {
        const { GoogleSignIn } = await import('@capawesome/capacitor-google-sign-in');
        await GoogleSignIn.signOut().catch(() => {});
      } catch (_) {}
    }
  } catch (err) {
    console.error('[Logout Teardown] Error during teardown:', err);
  } finally {
    if (typeof window !== 'undefined') {
      const target = redirectPath.startsWith('/#')
        ? redirectPath
        : (redirectPath.startsWith('/') ? `/#${redirectPath}` : `/#/${redirectPath}`);
      const cleanHash = target.startsWith('/#') ? target.substring(1) : target;
      try {
        window.location.hash = cleanHash;
      } catch (_) {}
      window.location.replace(target);
      window.location.reload();
    }
  }
};

// Clear sessions for other portals (keep only the specified portal)
export const clearOtherSessions = (keepPortal) => {
  if (keepPortal !== 'student') clearAuthTokens('student');
  if (keepPortal !== 'teacher') clearAuthTokens('teacher');
  if (keepPortal !== 'admin') clearAuthTokens('admin');
  if (keepPortal !== 'b2b') clearAuthTokens('b2b');
  if (keepPortal !== 'guardian') clearAuthTokens('guardian');
};

let activeRefreshPromise = null;
let activeRefreshResolver = null;

// Listen to the window storage event to synchronize token updates across multiple tabs instantly
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (event.key && (event.key.endsWith('_token') || event.key.endsWith('_refresh_token'))) {
      const portal = event.key.split('_')[0]; // e.g. 'student', 'teacher', 'admin'
      if (portal && activeRefreshPromise && activeRefreshResolver) {
        const newToken = localStorage.getItem(`${portal}_token`);
        if (newToken && !isTokenExpired(newToken)) {
          console.log(`[Multi-Tab Sync] Token updated in another tab for portal: ${portal}. Resolving local refresh promise.`);
          // Resolve the pending local promise with the new token to prevent duplicate API requests
          activeRefreshResolver(newToken);
        }
      }
    }
  });
}

// Refresh access token using refresh token (with active promise mutex lock and multi-tab synchronization)
export const refreshAccessToken = async (portal) => {
  if (isLoggingOut) {
    throw new Error('Logout in progress, token refresh aborted.');
  }

  if (!isLocalStorageAvailable()) {
    throw new Error('localStorage not available');
  }

  // If there's already an active refresh in progress, reuse its promise to prevent duplicate API requests
  if (activeRefreshPromise) {
    return activeRefreshPromise;
  }

  activeRefreshPromise = new Promise(async (resolve, reject) => {
    activeRefreshResolver = resolve;
    try {
      if (isLoggingOut) {
        throw new Error('Logout in progress, token refresh aborted.');
      }

      // Check if another tab refreshed the token while we were waiting/queuing
      const currentToken = getAuthToken(portal);
      if (currentToken && !isTokenExpired(currentToken)) {
        console.log('[Token Mutex] Valid token found in storage, skipping network refresh call');
        resolve(currentToken);
        return;
      }

      const refreshToken = getRefreshToken(portal);
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const fingerprint = await getBrowserFingerprint();
      const response = await axios.post(apiUrl('users/token/refresh/'), {
        refresh: refreshToken,
        fingerprint: fingerprint,
        ...(await getExtendedDeviceDetails())
      });

      if (isLoggingOut) {
        throw new Error('Logout in progress, skipping token persistence.');
      }

      const { access, refresh } = response.data;

      // Update tokens - rotation returns both access and refresh
      localStorage.setItem(tokenKey(portal), access);
      if (refresh) {
        localStorage.setItem(refreshKey(portal), refresh);
      }

      resolve(access);
    } catch (error) {
      console.error('Token refresh failed:', error);
      reject(error);
    } finally {
      activeRefreshPromise = null;
      activeRefreshResolver = null;
    }
  });

  return activeRefreshPromise;
};

// Get access token (exported for use in API config)
export const getAccessToken = (portal) => getAuthToken(portal);


// Get detailed device/platform info for analytics (Async for native support)
export const getExtendedDeviceDetails = async () => {
  if (typeof navigator === 'undefined') return { device_name: 'Unknown Device' };
  
  const userAgent = navigator.userAgent;
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
  const isNative = window.Capacitor?.isNativePlatform?.() || (typeof window !== 'undefined' && window.Capacitor?.getPlatform() !== 'web');
  
  let browser = 'Unknown';
  if (userAgent.indexOf("Firefox") > -1) browser = "Firefox";
  else if (userAgent.indexOf("SamsungBrowser") > -1) browser = "Samsung";
  else if (userAgent.indexOf("Opera") > -1 || userAgent.indexOf("OPR") > -1) browser = "Opera";
  else if (userAgent.indexOf("Edg") > -1) browser = "Edge";
  else if (userAgent.indexOf("Chrome") > -1) browser = "Chrome";
  else if (userAgent.indexOf("Safari") > -1) browser = "Safari";
  
  let os = 'Unknown';
  let os_version = '';
  let model_name = '';

  if (isNative) {
    try {
      const info = await Device.getInfo();
      os = info.operatingSystem === 'ios' ? 'iOS' : (row => row.charAt(0).toUpperCase() + row.slice(1))(info.operatingSystem || 'Android');
      os_version = info.osVersion;
      
      // Concat manufacturer and model for clearer ID on Android (e.g. "Xiaomi 23128PC33I")
      const mfr = info.manufacturer && info.manufacturer !== 'unknown' ? info.manufacturer : '';
      const mdl = info.model || '';
      model_name = mfr && !mdl.startsWith(mfr) ? `${mfr} ${mdl}` : mdl;
    } catch (e) {
      console.warn('Failed to get native device info:', e);
    }
  }

  // Fallback / Web OS detection
  if (!os || os === 'Unknown') {
    if (userAgent.indexOf("Win") > -1) {
      os = "Windows";
      const match = userAgent.match(/Windows NT ([\d.]+)/);
      if (match) os_version = match[1];
    } else if (userAgent.indexOf("Mac") > -1) {
      os = "macOS";
      const match = userAgent.match(/Mac OS X ([\d._]+)/);
      if (match) os_version = match[1]?.replace(/_/g, '.');
    } else if (userAgent.indexOf("Android") > -1) {
      os = "Android";
      const match = userAgent.match(/Android ([\d.]+)/);
      if (match) os_version = match[1];
    } else if (userAgent.indexOf("iPhone") > -1 || userAgent.indexOf("iPad") > -1) {
      os = "iOS";
      const match = userAgent.match(/OS ([\d._]+)/);
      if (match) os_version = match[1]?.replace(/_/g, '.');
    } else if (userAgent.indexOf("Linux") > -1) os = "Linux";
  }

  let app_type = 'browser';
  if (isNative) app_type = 'native';
  else if (isPWA) app_type = 'pwa';

  let platform = os.toLowerCase();
  if (platform === 'macos') platform = 'mac';

  // Construct a professional friendly name based on requirements
  let final_device_name = '';
  
  if (isNative) {
    // For Native: "Samsung Galaxy S24" or "iPhone 15 Pro"
    final_device_name = model_name || `${os} Device`;
  } else {
    // For PWA/Browser: "Safari on iOS (PWA)" or "Chrome on Windows (Browser)"
    const typeLabel = isPWA ? '(PWA)' : '(Browser)';
    final_device_name = `${browser} on ${os} ${typeLabel}`;
  }
  
  return {
    device_name: final_device_name,
    platform,
    app_type,
    os_version,
    model_name,
    browser_name: browser
  };
};

// Legacy sync support (limited)
export const getDeviceIdentifier = () => {
    const userAgent = navigator.userAgent;
    const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
    const isNative = window.Capacitor?.isNativePlatform?.() || (typeof window !== 'undefined' && window.Capacitor?.getPlatform() !== 'web');
    let os = 'Device';
    if (userAgent.indexOf("Win") > -1) os = "Windows";
    else if (userAgent.indexOf("Mac") > -1) os = "Mac";
    else if (userAgent.indexOf("Android") > -1) os = "Android";
    else if (userAgent.indexOf("iPhone") > -1) os = "iPhone";
    return `${os}${isPWA ? ' (PWA)' : (isNative ? ' (App)' : '')}`;
};

// One-time migration if older shared keys were used
export const migrateOldTokens = () => {
  if (!isLocalStorageAvailable()) return;
  try {
    const oldToken = localStorage.getItem('token');
    const oldRefresh = localStorage.getItem('refresh_token');
    const oldUser = localStorage.getItem('user');
    if (oldToken && oldUser) {
      try {
        const data = JSON.parse(oldUser);
        const portal = data?.user_type === 'teacher' ? 'teacher' : 'student';
        if (!getAuthToken(portal)) {
          setAuthTokens(portal, oldToken, oldRefresh || '', data);
        }
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('user');
      } catch { }
    }
  } catch (error) {
    console.error('Error migrating old tokens:', error);
  }
};

import { getFingerprint } from '@/services/fingerprint';

let cachedFingerprint = null;

export const getBrowserFingerprint = async () => {
  if (cachedFingerprint) return cachedFingerprint;

  // 1. Check if we have a persisted fingerprint in localStorage to prevent startup race conditions
  if (isLocalStorageAvailable()) {
    try {
      const stored = localStorage.getItem('fingerprint_id');
      if (stored) {
        cachedFingerprint = stored;
        return cachedFingerprint;
      }
    } catch (e) {
      console.warn('Failed to read fingerprint from localStorage:', e);
    }
  }

  const isNative = window.Capacitor?.isNativePlatform?.() || (typeof window !== 'undefined' && window.Capacitor?.getPlatform() !== 'web');
  let visitorId = null;
  
  // 2. Try native hardware identification on mobile platforms
  if (isNative) {
    try {
      const deviceId = await Device.getId();
      if (deviceId && deviceId.identifier) {
        visitorId = deviceId.identifier;
      }
    } catch (e) {
      console.warn('[Native Identification] Failed to get device ID, falling back to browser fingerprint:', e);
    }
  }

  // 3. Fallback to standard web/PWA browser fingerprinting
  if (!visitorId) {
    try {
      visitorId = await getFingerprint();
    } catch (error) {
      console.error('Error getting browser fingerprint:', error);
      visitorId = 'unknown_web_fingerprint';
    }
  }

  // 4. Persist the chosen fingerprint to localStorage and cached variable
  if (visitorId && isLocalStorageAvailable()) {
    try {
      localStorage.setItem('fingerprint_id', visitorId);
    } catch (e) {
      console.warn('Failed to write fingerprint to localStorage:', e);
    }
  }

  cachedFingerprint = visitorId;
  return cachedFingerprint;
};
export const clearFingerprintCache = () => {
  cachedFingerprint = null;
};
